import React, { useState, useEffect, useRef } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { CreditCard, Send, CheckCircle2, XCircle, Plug, MessageSquare, Syringe, Stethoscope, CalendarCheck, CalendarX, Wallet, DoorOpen, Cake, Pencil, Check, X, Plus, Info, RefreshCw } from 'lucide-react';
import { getPaytrSettings, updatePaytrSettings, getNetgsmSettings, updateNetgsmSettings, sendTestNetgsm, getNetgsmBalance } from '../../services/api';
import { toast } from 'sonner';
import api from '../../services/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const SMS_TYPES = [
  { key: 'vaccine_reminder', label: 'Aşı Hatırlatma', icon: Syringe, color: 'text-[#34C759]', bg: 'bg-green-50' },
  { key: 'treatment_reminder', label: 'Tedavi Hatırlatma', icon: Stethoscope, color: 'text-[#6b6de3]', bg: 'bg-blue-50' },
  { key: 'appointment_confirmed', label: 'Randevu Onayı', icon: CalendarCheck, color: 'text-[#007AFF]', bg: 'bg-sky-50' },
  { key: 'appointment_rejected', label: 'Randevu Reddi', icon: CalendarX, color: 'text-[#FF3B30]', bg: 'bg-red-50' },
  { key: 'balance_reminder', label: 'Bakiye Hatırlatma', icon: Wallet, color: 'text-[#FF9500]', bg: 'bg-orange-50' },
  { key: 'boarding_checkout', label: 'Konaklama Bildirimi', icon: DoorOpen, color: 'text-[#AF52DE]', bg: 'bg-purple-50' },
  { key: 'birthday_greeting', label: 'Doğum Günü Kutlama', icon: Cake, color: 'text-[#FF2D55]', bg: 'bg-pink-50' },
];

const TEMPLATE_PARAMS = [
  { code: '{customer_name}', label: 'Müşteri Adı' },
  { code: '{pet_name}', label: 'Hasta Adı' },
  { code: '{operation_name}', label: 'İşlem Adı' },
  { code: '{due_date}', label: 'Tarih' },
  { code: '{date}', label: 'Randevu Tarihi' },
  { code: '{amount}', label: 'Tutar' },
];

export const AdminIntegrationsPanel = () => {
  const [subTab, setSubTab] = useState('integrations');
  const [paytrSettings, setPaytrSettings] = useState({ merchant_id: '', merchant_key: '', merchant_salt: '', test_mode: true });
  const [netgsmSettings, setNetgsmSettings] = useState({ usercode: '', password: '', msgheader: '', is_active: false, iysfilter: '0', appname: 'AGROSVET' });
  const [testPhone, setTestPhone] = useState('');
  const [testMsg, setTestMsg] = useState('AGROSVET test mesajı');
  const [testSending, setTestSending] = useState(false);
  const [netgsmSaving, setNetgsmSaving] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceData, setBalanceData] = useState(null);

  // SMS Templates
  const [templates, setTemplates] = useState(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [templateSaving, setTemplateSaving] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    getPaytrSettings().then(r => setPaytrSettings(r.data)).catch(() => {});
    getNetgsmSettings().then(r => {
      const d = r.data;
      setNetgsmSettings({
        usercode: d.usercode || '',
        password: '',
        msgheader: d.msgheader || '',
        is_active: !!d.is_active,
        iysfilter: d.iysfilter || '0',
        appname: d.appname || 'AGROSVET',
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (subTab === 'sms' && !templates) {
      setTemplatesLoading(true);
      api.get('/settings/sms-templates')
        .then(r => setTemplates(r.data))
        .catch(() => toast.error('Şablonlar yüklenemedi'))
        .finally(() => setTemplatesLoading(false));
    }
  }, [subTab, templates]);

  const handleSaveNetgsm = async () => {
    setNetgsmSaving(true);
    try {
      const payload = { ...netgsmSettings };
      if (!payload.password) delete payload.password;
      await updateNetgsmSettings(payload);
      toast.success('NetGSM ayarları kaydedildi');
    } catch { toast.error('Kaydetme hatası'); }
    finally { setNetgsmSaving(false); }
  };

  const handleCheckBalance = async () => {
    setBalanceLoading(true);
    setBalanceData(null);
    try {
      const res = await getNetgsmBalance();
      if (res.data.success) {
        setBalanceData({ success: true, data: res.data.data });
        toast.success('Bağlantı başarılı');
      } else {
        setBalanceData({ success: false, message: res.data.message, code: res.data.code });
        toast.error(res.data.message || 'Bağlantı hatası');
      }
    } catch { toast.error('Bağlantı testi başarısız'); }
    finally { setBalanceLoading(false); }
  };

  const handleTestSms = async () => {
    if (!testPhone.trim()) { toast.error('Telefon numarası girin'); return; }
    setTestSending(true);
    try {
      const res = await sendTestNetgsm({ phone: testPhone, message: testMsg });
      if (res.data.success) toast.success('Test SMS gönderildi');
      else toast.error(res.data.message || res.data.error || 'Gönderilemedi');
    } catch { toast.error('Hata oluştu'); }
    finally { setTestSending(false); }
  };

  const insertParam = (code) => {
    const ta = textareaRef.current;
    if (!ta) {
      setEditValue(prev => prev + code);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const newVal = editValue.substring(0, start) + code + editValue.substring(end);
    setEditValue(newVal);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + code.length, start + code.length);
    }, 0);
  };

  const startEdit = (key, currentTemplate) => {
    setEditingKey(key);
    setEditValue(currentTemplate || '');
  };

  const saveTemplate = async (key) => {
    setTemplateSaving(true);
    try {
      await api.put('/settings/sms-templates', { [key]: editValue });
      setTemplates(prev => ({ ...prev, [key]: editValue }));
      setEditingKey(null);
      toast.success('Şablon kaydedildi');
    } catch {
      toast.error('Kaydetme başarısız');
    } finally {
      setTemplateSaving(false);
    }
  };

  // Render template text with highlighted params
  const renderTemplate = (text) => {
    if (!text) return <span className="italic text-[#C7C7CC]">Şablon tanımlanmamış</span>;
    const parts = text.split(/(\{[^}]+\})/g);
    return parts.map((part, i) => {
      const param = TEMPLATE_PARAMS.find(p => p.code === part);
      if (param) {
        return <span key={i} className="inline-block bg-[#6b6de3]/10 text-[#6b6de3] text-[10px] font-semibold px-1.5 py-0.5 rounded mx-0.5">{param.label}</span>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div className="space-y-4">
      {/* Sub-tab selector */}
      <div className="bg-[#F2F2F7] rounded-xl p-1 flex gap-1">
        <button
          onClick={() => setSubTab('integrations')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-medium transition-all ${subTab === 'integrations' ? 'bg-white text-[#6b6de3] shadow-sm' : 'text-[#8E8E93]'}`}
          data-testid="subtab-integrations"
        >
          <Plug size={15} /> Entegrasyonlar
        </button>
        <button
          onClick={() => setSubTab('sms')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-medium transition-all ${subTab === 'sms' ? 'bg-white text-[#6b6de3] shadow-sm' : 'text-[#8E8E93]'}`}
          data-testid="subtab-sms"
        >
          <MessageSquare size={15} /> SMS Şablonları
        </button>
      </div>

      {/* Integrations Tab */}
      {subTab === 'integrations' && (
        <>
          {/* NetGSM */}
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden border border-gray-100">
                  <img src="/netgsm-logo.svg" alt="NetGSM" className="w-8 h-8 object-contain" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">NetGSM</h3>
                  <p className="text-xs text-[#8E8E93]">SMS ve OTP gönderim entegrasyonu</p>
                </div>
              </div>
              <button onClick={() => setNetgsmSettings(p => ({ ...p, is_active: !p.is_active }))} className="transition-colors">
                {netgsmSettings.is_active
                  ? <span className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 text-green-600"><CheckCircle2 size={12} /> SMS Aktif</span>
                  : <span className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-500"><XCircle size={12} /> Pasif</span>}
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-[#8E8E93]">Abone No (usercode)</Label>
                <Input value={netgsmSettings.usercode} onChange={e => setNetgsmSettings(p => ({ ...p, usercode: e.target.value }))}
                  placeholder="NetGSM abone numaranız"
                  className="h-11 bg-[#F2F2F7] border-0 rounded-xl" data-testid="netgsm-usercode" />
              </div>
              <div>
                <Label className="text-xs text-[#8E8E93]">API Alt Kullanıcı Şifresi</Label>
                <Input type="password" value={netgsmSettings.password} onChange={e => setNetgsmSettings(p => ({ ...p, password: e.target.value }))}
                  placeholder="Değiştirmek için doldurun"
                  className="h-11 bg-[#F2F2F7] border-0 rounded-xl" data-testid="netgsm-password" />
                <p className="text-[10px] text-[#C7C7CC] mt-1">&quot;API Kullanıcısı&quot; tipli alt kullanıcı şifresi</p>
              </div>
              <div>
                <Label className="text-xs text-[#8E8E93]">Gönderici Adı (msgheader)</Label>
                <Input value={netgsmSettings.msgheader} onChange={e => setNetgsmSettings(p => ({ ...p, msgheader: e.target.value }))}
                  placeholder="NetGSM panelinde onaylı gönderici adı"
                  className="h-11 bg-[#F2F2F7] border-0 rounded-xl" data-testid="netgsm-msgheader" />
              </div>
              <div>
                <Label className="text-xs text-[#8E8E93]">İYS Filtresi</Label>
                <Select value={netgsmSettings.iysfilter} onValueChange={v => setNetgsmSettings(p => ({ ...p, iysfilter: v }))}>
                  <SelectTrigger className="h-11 bg-[#F2F2F7] border-0 rounded-xl" data-testid="netgsm-iysfilter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Bilgilendirme (İYS kontrolü yok)</SelectItem>
                    <SelectItem value="11">Bireysel — Ticari (İYS kontrollü)</SelectItem>
                    <SelectItem value="12">Tacir — Ticari (İYS kontrollü)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-[#C7C7CC] mt-1">OTP ve bilgilendirme için &quot;Bilgilendirme&quot; seçin</p>
              </div>
              <div>
                <Label className="text-xs text-[#8E8E93]">Uygulama Adı (appname)</Label>
                <Input value={netgsmSettings.appname} onChange={e => setNetgsmSettings(p => ({ ...p, appname: e.target.value }))}
                  placeholder="AGROSVET"
                  className="h-11 bg-[#F2F2F7] border-0 rounded-xl" data-testid="netgsm-appname" />
                <p className="text-[10px] text-[#C7C7CC] mt-1">NetGSM raporlarında görünür</p>
              </div>

              <div className="flex gap-2 pt-1">
                <Button onClick={handleSaveNetgsm} disabled={netgsmSaving} className="flex-1 bg-emerald-500 hover:bg-emerald-600 rounded-full" data-testid="netgsm-save-btn">
                  {netgsmSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
                <Button onClick={handleCheckBalance} disabled={balanceLoading} variant="outline" className="flex-1 rounded-full" data-testid="netgsm-balance-btn">
                  {balanceLoading ? <RefreshCw size={14} className="animate-spin mr-1" /> : null}
                  {balanceLoading ? 'Sorgulanıyor...' : 'Bakiye / Bağlantı Testi'}
                </Button>
              </div>

              {balanceData && (
                balanceData.success ? (
                  <div className="bg-green-50 rounded-xl p-3 border border-green-100">
                    <p className="text-xs font-medium text-green-700 mb-1">Bağlantı Başarılı</p>
                    <pre className="text-[10px] text-green-600 whitespace-pre-wrap break-all">{JSON.stringify(balanceData.data, null, 2)}</pre>
                  </div>
                ) : (
                  <div className="bg-red-50 rounded-xl p-3 border border-red-100">
                    <p className="text-xs font-medium text-red-700 mb-1">Bağlantı Başarısız {balanceData.code ? `(Kod: ${balanceData.code})` : ''}</p>
                    <p className="text-[11px] text-red-600">{balanceData.message}</p>
                  </div>
                )
              )}

              <div className="border-t pt-3 mt-1">
                <p className="text-xs font-medium text-gray-500 mb-2">Bağlantı Testi</p>
                <div className="flex gap-2">
                  <Input value={testPhone} onChange={e => setTestPhone(e.target.value)}
                    placeholder="05XX XXX XX XX"
                    className="h-10 bg-[#F2F2F7] border-0 rounded-xl flex-1" data-testid="netgsm-test-phone" />
                  <Button onClick={handleTestSms} disabled={testSending} size="sm" variant="outline" className="rounded-xl h-10 px-3" data-testid="netgsm-test-btn">
                    {testSending
                      ? <span className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                      : <Send size={14} />}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* PayTR */}
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center"><CreditCard size={20} className="text-blue-600" /></div>
                <div><h3 className="font-semibold text-gray-900">PayTR</h3><p className="text-xs text-[#8E8E93]">Online ödeme sistemi</p></div>
              </div>
              <button onClick={() => setPaytrSettings(p => ({ ...p, test_mode: !p.test_mode }))} className="transition-colors">
                {paytrSettings.test_mode ? <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-600">Test</span> : <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 text-green-600">Canlı</span>}
              </button>
            </div>
            <div className="space-y-3">
              <div><Label className="text-xs text-[#8E8E93]">Merchant ID</Label><Input value={paytrSettings.merchant_id} onChange={e => setPaytrSettings(p => ({ ...p, merchant_id: e.target.value }))} className="h-11 bg-[#F2F2F7] border-0 rounded-xl" /></div>
              <div><Label className="text-xs text-[#8E8E93]">Merchant Key</Label><Input type="password" value={paytrSettings.merchant_key} onChange={e => setPaytrSettings(p => ({ ...p, merchant_key: e.target.value }))} className="h-11 bg-[#F2F2F7] border-0 rounded-xl" /></div>
              <div><Label className="text-xs text-[#8E8E93]">Merchant Salt</Label><Input type="password" value={paytrSettings.merchant_salt} onChange={e => setPaytrSettings(p => ({ ...p, merchant_salt: e.target.value }))} className="h-11 bg-[#F2F2F7] border-0 rounded-xl" /></div>
              <Button onClick={async () => { try { await updatePaytrSettings(paytrSettings); toast.success('Kaydedildi'); } catch { toast.error('Hata'); } }}
                className="w-full bg-[#6b6de3] rounded-full">Kaydet</Button>
            </div>
          </div>
        </>
      )}

      {/* SMS Templates Tab */}
      {subTab === 'sms' && (
        <>
          <div className="bg-blue-50 rounded-2xl p-3 flex items-start gap-2.5">
            <Info size={16} className="text-[#007AFF] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-[#007AFF]">SMS Şablon Yönetimi</p>
              <p className="text-[10px] text-[#007AFF]/70 mt-0.5">Tüm kliniklere uygulanan SMS mesaj şablonlarını buradan düzenleyebilirsiniz. Klinikler sadece hangi SMS tiplerini göndereceğini seçer.</p>
            </div>
          </div>

          {templatesLoading ? (
            <div className="text-center py-8 text-[#8E8E93] text-sm">Yükleniyor...</div>
          ) : (
            SMS_TYPES.map(type => {
              const Icon = type.icon;
              const template = templates?.[type.key] || '';
              const isEditing = editingKey === type.key;

              return (
                <div key={type.key} className="bg-white rounded-2xl p-4 shadow-sm" data-testid={`admin-sms-${type.key}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-9 h-9 rounded-xl ${type.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon size={18} className={type.color} />
                    </div>
                    <p className="text-sm font-semibold text-gray-900 flex-1">{type.label}</p>
                    {!isEditing && (
                      <button onClick={() => startEdit(type.key, template)} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid={`edit-${type.key}`}>
                        <Pencil size={14} className="text-[#8E8E93]" />
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="space-y-2">
                      <textarea
                        ref={textareaRef}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        rows={3}
                        className="w-full text-sm bg-[#F2F2F7] border-0 rounded-xl px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-[#6b6de3]/30"
                        data-testid={`template-input-${type.key}`}
                        autoFocus
                      />
                      {/* Clickable param chips */}
                      <div className="flex flex-wrap gap-1.5">
                        {TEMPLATE_PARAMS.map(p => (
                          <button
                            key={p.code}
                            onClick={() => insertParam(p.code)}
                            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium bg-[#6b6de3]/10 text-[#6b6de3] hover:bg-[#6b6de3]/20 transition-colors"
                            data-testid={`param-${p.code}`}
                          >
                            <Plus size={10} /> {p.label}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={() => setEditingKey(null)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-[#8E8E93] hover:bg-gray-100">
                          <X size={14} /> Vazgeç
                        </button>
                        <button
                          onClick={() => saveTemplate(type.key)}
                          disabled={templateSaving}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-[#6b6de3] hover:bg-[#5b5dd3] disabled:opacity-50"
                          data-testid={`save-${type.key}`}
                        >
                          <Check size={14} /> Kaydet
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-[#8E8E93] bg-[#F2F2F7] rounded-xl px-3 py-2.5 leading-relaxed">
                      {renderTemplate(template)}
                    </p>
                  )}
                </div>
              );
            })
          )}
        </>
      )}
    </div>
  );
};
